import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Tabs, Tab } from '@material-ui/core';
import FeaturePageShell from '../../components/shell/FeaturePageShell';
import SearchFeaturePageShell from '../../components/shell/SearchFeaturePageShell';
import SaveActionBtnGrp from '../../components/kea/settings/SaveActionBtnGrp';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../actions/Notifications';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import { normalizeRuntimeSaveOutcome } from '../../app/runtime/saveOutcome';
import { pluginAppInIframe } from '../../util/appUtils';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { usePreviewOpener } from '../shell/preview/usePreviewOpener';
import { useBundleRebuild } from '../home/components/Diagnostics/useBundleRebuild';
import PublishProgressDialog from '../home/components/PublishProgressDialog';
import {
  useOperationDialogVisibility,
} from '../home/components/PublishProgressDialog/useOperationDialogVisibility';
import { notifyOperatorStateRefresh } from '../home/livePublishEvents';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import SearchBoxSettings from './tabs/SearchBoxSettings';
import ResultsPageSettings from './tabs/ResultsPageSettings';
import ThemeSettings from './tabs/ThemeSettings';
import DataSettings from './tabs/DataSettings';
import styles from './SearchSettingsPage.module.scss';
import IntlMessages from 'util/IntlMessages';

const ALL_TABS = [
  {
    id: 'searchbox', labelKey: 'search.page.searchBox.title', component: SearchBoxSettings, requiredViews: ['searchBox', 'dropdown'],
  },
  {
    id: 'results-page', labelKey: 'search.page.resultsPage.title', component: ResultsPageSettings, requiredViews: ['resultsPage'],
  },
  {
    id: 'theme', labelKey: 'search.page.theme.title', component: ThemeSettings, requiredViews: [],
  },
  {
    id: 'data', labelKey: 'search.page.dataConnectivity.title', component: DataSettings, requiredViews: [],
  },
];

const LEGACY_TABS = ALL_TABS.filter((tab) => !['theme', 'data'].includes(tab.id));

const SEARCH_EXPERIENCE_ALIASES = {
  unified: 'unified',
  widget: 'unified',
  search_widget: 'unified',
  searchWidget: 'unified',
  classic: 'classic',
  fullpage: 'fullpage',
  full_page: 'fullpage',
};

const TABS_BY_EXPERIENCE = {
  unified: ['searchbox', 'results-page'],
  classic: ['searchbox', 'results-page'],
  fullpage: ['searchbox', 'results-page'],
};

const PAGE_DESCRIPTION_KEYS = {
  searchbox: 'search.page.searchBox.description',
  'results-page': 'search.page.resultsPage.description',
  theme: 'search.page.theme.description',
  data: 'search.page.dataConnectivity.description',
};

const normalizeSearchExperience = (mode) => {
  if (!mode || typeof mode !== 'string') return null;
  return SEARCH_EXPERIENCE_ALIASES[mode] || null;
};

const isTabAllowedForExperience = (tabId, mode) => {
  if (tabId === 'theme' || tabId === 'data') return true;
  const normalizedMode = normalizeSearchExperience(mode);
  if (!normalizedMode) return true;
  return TABS_BY_EXPERIENCE[normalizedMode]?.includes(tabId) ?? true;
};

const getRequiredViews = (tab) => (
  Array.isArray(tab.requiredViews)
    ? tab.requiredViews
    : (tab.requiredView ? [tab.requiredView] : [])
);

const tabMatchesAvailableViews = (tab, availableViews) => {
  const requiredViews = getRequiredViews(tab);
  if (requiredViews.length === 0) return true;
  if (availableViews.length === 0) return true;
  return requiredViews.some((view) => availableViews.includes(view));
};

const getTabIdFromUrlSegment = (urlSegment) => {
  const mapping = {
    searchbox: 'searchbox',
    'result-page': 'results-page',
    theme: 'theme',
  };
  return mapping[urlSegment] || urlSegment;
};

const getUrlSegmentFromTabId = (tabId) => {
  const mapping = {
    'results-page': 'result-page',
  };
  return mapping[tabId] || tabId;
};

const buildSettingsPath = (pathname, newTabId) => {
  const pathSegments = pathname.split('/');
  const settingsIndex = pathSegments.findIndex((seg) => seg === 'settings');
  if (settingsIndex === -1) return pathname;
  return [...pathSegments.slice(0, settingsIndex + 1), getUrlSegmentFromTabId(newTabId)].join('/');
};

const SearchSettingsPage = ({
  match, history, location, client, standaloneTabId,
}) => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const currentShop = useSelector((s) => s.app.currentShop);
  const shopId = currentShop;
  const {
    gatewayClient: shellGatewayClient,
    searchSettingsClient,
    capabilities,
    productProfile,
    runtime,
  } = useShellAppContext();
  const effectiveClient = client || searchSettingsClient || shellGatewayClient;
  const isStandalone = Boolean(standaloneTabId);
  const isEmbedded = pluginAppInIframe();

  // All hooks must be called before any early return (Rules of Hooks).
  const searchExperience = useSelector((s) => s.searchExperience);
  const availableViews = useMemo(() => {
    const views = searchExperience?.config?.search?.views || {};
    return Object.keys(views);
  }, [searchExperience]);
  const currentSearchMode = searchExperience?.config?.search?.searchExperience || null;

  const getTabLabel = (tab) => {
    if (tab.id === 'searchbox') {
      const normalizedMode = normalizeSearchExperience(currentSearchMode);
      if (normalizedMode) {
        return localizeMessage(normalizedMode === 'classic'
          ? 'sidebar.search.uiSettings.searchDropdown'
          : 'sidebar.search.uiSettings.searchWidget');
      }
    }
    return localizeMessage(tab.labelKey);
  };

  const TABS = useMemo(() => {
    if (isStandalone) {
      return ALL_TABS
        .filter((tab) => tab.id === standaloneTabId)
        .filter((tab) => isTabAllowedForExperience(tab.id, currentSearchMode))
        .filter((tab) => tabMatchesAvailableViews(tab, availableViews));
    }

    // Embedded (bridge-only) path: legacy settings tabs require transport that
    // is not available in the production shell. Theme is a standalone page and
    // is handled by its own exact route before this generic settings route.
    if (isEmbedded) return [];

    const tabsForMode = LEGACY_TABS.filter(
      (tab) => isTabAllowedForExperience(tab.id, currentSearchMode),
    );
    if (availableViews.length === 0) return tabsForMode;
    return tabsForMode.filter((tab) => tabMatchesAvailableViews(tab, availableViews));
  }, [availableViews, currentSearchMode, isEmbedded, isStandalone, standaloneTabId]);

  const pathParts = location.pathname.split('/');
  const tabFromPath = pathParts[pathParts.length - 1];
  const initialTab = standaloneTabId || getTabIdFromUrlSegment(tabFromPath);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewPipelinePending, setIsPreviewPipelinePending] = useState(false);
  const {
    canOpenPreview,
    openStorefrontPreview,
  } = usePreviewOpener();

  const tabRefs = useRef({});
  const previewBuild = useBundleRebuild();
  const previewProgressStatus = previewBuild.status
    || (isPreviewPipelinePending ? 'queued' : null);
  const previewDialog = useOperationDialogVisibility(previewProgressStatus);
  const previewBuildSupported = runtime?.supports?.(
    ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
  ) === true;

  const redirectToAvailableSurface = () => {
    const fallbackTab = isEmbedded && !isStandalone
      ? 'theme'
      : TABS[0]?.id || ALL_TABS.find((tab) => isTabAllowedForExperience(tab.id, currentSearchMode))?.id;
    if (!fallbackTab) return;
    const newPath = buildSettingsPath(location.pathname, fallbackTab);
    history.replace(newPath);
  };

  useEffect(() => {
    const newTab = standaloneTabId || getTabIdFromUrlSegment(tabFromPath);
    const isTabAvailable = TABS.some((t) => t.id === newTab);

    if (!isTabAvailable) {
      redirectToAvailableSurface();
      return;
    }

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [tabFromPath, TABS, activeTab, history, location.pathname, standaloneTabId, isEmbedded, currentSearchMode]);

  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
    const newPath = buildSettingsPath(location.pathname, newTab);
    history.push(newPath);
  };

  const sendRuntimeCommand = async (command, payload = {}) => {
    if (!runtime?.supports?.(command)) {
      throw new Error(`Admin runtime does not support ${command}.`);
    }
    return runtime.sendMessageWithResponse(command, { shopId, ...payload });
  };

  const handleSave = async () => {
    const activeTabRef = tabRefs.current[activeTab];
    if (!activeTabRef?.save) return;

    const showPreviewProgress = previewBuildSupported
      && !(activeTab === 'data' && productProfile?.id === 'cloud');

    setIsSaving(true);
    if (showPreviewProgress) {
      setIsPreviewPipelinePending(true);
      if (typeof previewBuild.reset === 'function') {
        previewBuild.reset();
      }
      previewDialog.begin();
    }

    try {
      const saveResult = await activeTabRef.save();
      const saveOutcome = normalizeRuntimeSaveOutcome(saveResult);
      if (!saveOutcome.success || saveOutcome.errors.length > 0) {
        throw new Error(
          saveOutcome.errors[0]?.message
          || saveResult?.error
          || localizeMessage('operation.preview.saveFailed'),
        );
      }
      if (activeTab === 'data' && productProfile?.id === 'cloud') {
        setHasChanges(false);
        dispatch(enqueueSuccessSnackbar(localizeMessage('search.page.dataConnectivity.saved')));
        return;
      }
      // The product adapter owns any plugin-only dirty/checkpoint side effects.
      await sendRuntimeCommand(ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED);
      setHasChanges(false);
      notifyOperatorStateRefresh();
      if (!previewBuildSupported) {
        dispatch(enqueueSuccessSnackbar(localizeMessage('notifications.success.saved')));
        return;
      }
      await previewBuild.startPreviewBuild(shopId);
    } catch (error) {
      if (showPreviewProgress) {
        previewDialog.close();
      }
      dispatch(enqueueErrorSnackbar(error.message || localizeMessage('operation.preview.saveFailed')));
    } finally {
      setIsPreviewPipelinePending(false);
      setIsSaving(false);
    }
  };

  const handleDiscard = async () => {
    const activeTabRef = tabRefs.current[activeTab];
    if (!activeTabRef?.reload) return;

    try {
      await activeTabRef.reload();
      setHasChanges(false);
      dispatch(enqueueSuccessSnackbar(localizeMessage('common.changesDiscarded')));
    } catch (error) {
      dispatch(enqueueErrorSnackbar(localizeMessage('common.reloadFailed')));
    }
  };

  const registerTabRef = (tabId, ref) => {
    tabRefs.current[tabId] = ref;
  };

  // All hooks complete — early returns go here (Rules of Hooks compliance).
  if (!effectiveClient) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '2rem',
        }}
        data-testid="shell-client-missing"
      >
        <p>
          <IntlMessages id="ui.searchSettings.searchSettingsPage.sessionUnavailable" />
          {' '}
          <a href="/embedded/app"><IntlMessages id="ui.searchSettings.searchSettingsPage.retry" /></a>
        </p>
      </div>
    );
  }

  if (capabilities?.search === false) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '2rem',
        }}
        data-testid="capability-disabled"
      >
        <p>
          <IntlMessages id="ui.searchSettings.searchSettingsPage.searchSettingsAreNotAvailableForThisShop" />
          {' '}
          <a href="/embedded/app"><IntlMessages id="ui.searchSettings.searchSettingsPage.retry" /></a>
        </p>
      </div>
    );
  }

  const activeTabConfig = TABS.find((t) => t.id === activeTab);
  const ActiveTabComponent = activeTabConfig?.component;
  const pageTitle = isStandalone && activeTabConfig
    ? getTabLabel(activeTabConfig)
    : localizeMessage('search.page.settings.title');
  const pageWidth = activeTab === 'theme' ? 'wide' : 'expanded';
  const usesMappedThemeShell = isStandalone && activeTab === 'theme';
  const PageShell = usesMappedThemeShell ? SearchFeaturePageShell : FeaturePageShell;
  const pageShellProps = usesMappedThemeShell
    ? { pageId: 'theme' }
    : { title: pageTitle, width: pageWidth };

  return (
    <PageShell
      {...pageShellProps}
      description={localizeMessage(PAGE_DESCRIPTION_KEYS[activeTab])}
      actions={(
        <SaveActionBtnGrp
          isFetching={isSaving}
          disabled={!hasChanges}
          buttons={[
            {
              id: 'save',
              text: localizeMessage('common.save'),
              action: handleSave,
              color: 'primary',
              testId: `settings-${activeTab}-save-preview`,
            },
            {
              id: 'discard',
              text: localizeMessage('common.discard'),
              action: handleDiscard,
              color: 'secondary',
            },
          ]}
        />
      )}
    >

      <div className={styles.root}>
        {!isStandalone && TABS.length > 0 && (
          <Box className={styles.tabsContainer}>
            <Tabs
              value={TABS.some((t) => t.id === activeTab) ? activeTab : (TABS[0]?.id ?? activeTab)}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="standard"
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              {TABS.map((tab) => (
                <Tab className={styles.tab} key={tab.id} value={tab.id} label={getTabLabel(tab)} />
              ))}
            </Tabs>
          </Box>
        )}

        <Box className={styles.content}>
          {ActiveTabComponent && (
            <ActiveTabComponent
              client={effectiveClient}
              shopId={shopId}
              onRegisterRef={(ref) => registerTabRef(activeTab, ref)}
              onHasChanges={setHasChanges}
            />
          )}
        </Box>
      </div>

      {previewBuildSupported && <PublishProgressDialog
        open={previewDialog.open}
        operation={previewBuild.operation || 'preview'}
        status={previewProgressStatus}
        error={previewBuild.error}
        onRetry={() => {
          if (typeof previewBuild.reset === 'function') {
            previewBuild.reset();
          }
          previewDialog.begin();
          previewBuild.startPreviewBuild(shopId);
        }}
        onClose={previewDialog.close}
        onOpenPreview={openStorefrontPreview}
        canOpenPreview={canOpenPreview}
        localizeMessage={localizeMessage}
      />}
    </PageShell>
  );
};

SearchSettingsPage.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func,
    saveSearchExperienceConfig: PropTypes.func,
  }),
  standaloneTabId: PropTypes.string,
};

SearchSettingsPage.defaultProps = {
  client: null,
  standaloneTabId: null,
};

export default SearchSettingsPage;
